import React from 'react';
import { 
  Paper, 
  Stack, 
  Box, 
  Typography, 
  Button 
} from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  AIChatMessage,
  AIChatInput,
  AIChatSuggestions,
  AIChatLoader,
} from '@personal-dashboard/ui';

interface Message {
  id: string;
  sender: string;
  content: string;
  createdAt: string;
}

interface ActiveLesson {
  id: string;
  title: string;
  messages: Message[];
}

interface ActiveChatWorkspaceProps {
  activeLesson: ActiveLesson;
  inputText: string;
  onInputChange: (v: string) => void;
  isSending: boolean;
  onSendMessage: (text: string) => void;
  onEndLesson: () => void;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
}

const generalChips = [
  { label: '👋 Say Hej!', text: "Hej Sven! Introduce yourself and let's start with some basic greetings." },
  { label: '📝 Pronoun Quiz', text: 'Sven, please give me a quick 3-question multiple choice quiz on Swedish pronouns!' },
  { label: '💡 En vs Ett Rule', text: 'Can you explain the difference between "En" and "Ett" in Swedish, and give some rules of thumb?' },
  { label: '🇸🇪 Practice Dialogue', text: "Sven, let's practice a simple coffee shop dialogue in Swedish! You start!" },
];

const ActiveChatWorkspace: React.FC<ActiveChatWorkspaceProps> = ({
  activeLesson,
  inputText,
  onInputChange,
  isSending,
  onSendMessage,
  onEndLesson,
  messagesEndRef
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        height: 550,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Session Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            🇸🇪 Sven — Swedish Tutor
          </Typography>
          <Typography variant="caption" color="success.main" sx={{ fontWeight: 'medium' }}>
            ● Active Lesson: {activeLesson.title}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="error"
          startIcon={<HighlightOffIcon />}
          onClick={onEndLesson}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          End Session
        </Button>
      </Stack>

      {/* Chat Messages */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {activeLesson.messages.map((message) => (
          <AIChatMessage
            key={message.id}
            sender={message.sender}
            content={message.content}
            senderName={message.sender === 'assistant' ? 'Sven' : 'You'}
          />
        ))}
        {isSending && <AIChatLoader label="Sven is thinking..." />}
        <div ref={messagesEndRef} />
      </Box>

      {/* Suggestions Chips & Input Box */}
      <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        {!isSending && (
          <Box sx={{ mb: 1.5 }}>
            <AIChatSuggestions chips={generalChips} onClick={onSendMessage} />
          </Box>
        )}
        <AIChatInput
          value={inputText}
          onChange={onInputChange}
          onSubmit={onSendMessage}
          disabled={isSending}
          placeholder="Ask Sven any Swedish vocabulary, translation, or grammar question..."
        />
      </Box>
    </Paper>
  );
};

export default ActiveChatWorkspace;
